import * as React from 'react';
import { createPortal } from 'react-dom';

import './RequestStep.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faClipboard, faClipboardCheck, faXmark } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { Button, Menu, MenuItem, Tag } from '../design-system/components';

import { FieldGuideMappedRequest } from '../../Store/FieldGuide';

type RequestPickerProps = {
    requests: FieldGuideMappedRequest[];
    selected: FieldGuideMappedRequest | undefined;
    disabled: boolean;
    placeholder: string;
    clearLabel: string;
    onSelect: (request: FieldGuideMappedRequest | undefined) => void;
};

type MenuPosition = { top?: number; bottom?: number; maxHeight?: number; left: number; width: number };

const MenuMaxWidth = 528;
const MenuMaxHeight = 360;
const ViewportGap = 8;

/** Closes whichever request menu is currently open, so at most one is open at a time. */
let closeOpenMenu: (() => void) | undefined;

/**
 * Anchors the menu to the trigger's right edge, below it, or above it when there is no room below. Opening upward
 * anchors the menu's bottom edge, so a menu shorter than its maximum height still sits right above the trigger.
 */
function positionMenu(trigger: HTMLElement): MenuPosition {
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(MenuMaxWidth, window.innerWidth - 2 * ViewportGap);
    const left = Math.max(ViewportGap, Math.min(rect.right - width, window.innerWidth - width - ViewportGap));
    const below = rect.bottom + ViewportGap;
    if (below + MenuMaxHeight <= window.innerHeight - ViewportGap) {
        return { top: below, left, width };
    }

    return {
        bottom: window.innerHeight - rect.top + ViewportGap,
        maxHeight: Math.min(MenuMaxHeight, Math.max(0, rect.top - 2 * ViewportGap)),
        left,
        width,
    };
}

/** The menu is portaled out of the scrolling step content, which would clip it, to the themed app root. */
export const RequestPicker: React.FC<RequestPickerProps> = (props): React.ReactElement => {
    const { requests, selected, disabled, placeholder, clearLabel, onSelect } = props;

    const [position, setPosition] = React.useState<MenuPosition | undefined>(undefined);
    // The design-system Button does not forward a ref, so the wrapping elements are what the menu is positioned from.
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const open = position !== undefined;

    const reposition = React.useCallback((): void => {
        if (triggerRef.current) {
            setPosition(positionMenu(triggerRef.current));
        }
    }, []);

    const triggerId = React.useId();

    const menuItemElements = React.useCallback(
        (): HTMLElement[] => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
        []
    );

    const closeMenu = React.useCallback((): void => setPosition(undefined), []);

    const closeMenuAndFocusTrigger = React.useCallback((): void => {
        closeMenu();
        triggerRef.current?.querySelector('button')?.focus();
    }, [closeMenu]);

    React.useEffect(() => {
        if (open) {
            menuItemElements()[0]?.focus();
        }
    }, [open, menuItemElements]);

    React.useEffect(() => {
        if (!open) {
            return undefined;
        }

        closeOpenMenu?.();
        closeOpenMenu = closeMenu;

        const onPointerDown = (event: MouseEvent): void => {
            const target = event.target as Node;
            if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
                closeMenu();
            }
        };
        const onKeyDown = (event: KeyboardEvent): void => {
            if (!menuRef.current?.contains(document.activeElement)) {
                return;
            }

            if (event.key === 'Tab') {
                closeMenu();
                return;
            }

            if (event.key === 'Escape') {
                closeMenuAndFocusTrigger();
                return;
            }

            const items = menuItemElements();
            if (items.length === 0) {
                return;
            }

            const current = items.indexOf(document.activeElement as HTMLElement);
            let target: number | undefined;
            if (event.key === 'ArrowDown') {
                target = (current + 1) % items.length;
            } else if (event.key === 'ArrowUp') {
                target = current <= 0 ? items.length - 1 : current - 1;
            } else if (event.key === 'Home') {
                target = 0;
            } else if (event.key === 'End') {
                target = items.length - 1;
            }

            if (target !== undefined) {
                event.preventDefault();
                items[target].focus();
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);

        return () => {
            if (closeOpenMenu === closeMenu) {
                closeOpenMenu = undefined;
            }
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [open, reposition, closeMenu, closeMenuAndFocusTrigger, menuItemElements]);

    const closeWhenFocusLeaves = (event: React.FocusEvent): void => {
        const next = event.relatedTarget;
        if (next instanceof Node && !menuRef.current?.contains(next) && !triggerRef.current?.contains(next)) {
            closeMenu();
        }
    };

    const choose = (request: FieldGuideMappedRequest | undefined): void => {
        closeMenuAndFocusTrigger();
        onSelect(request);
    };

    const toggle = (): void => {
        if (open) {
            closeMenu();
        } else {
            reposition();
        }
    };

    return (
        <div ref={triggerRef} className={'page-pbc-picker'}>
            <Button
                id={triggerId}
                type={'floating'}
                className={'page-pbc-picker-btn'}
                aria-haspopup={'menu'}
                aria-expanded={open}
                disabled={disabled}
                leadingIcon={<FontAwesomeIcon icon={selected ? faClipboardCheck : faClipboard} />}
                trailingIcon={<FontAwesomeIcon icon={faAngleDown} />}
                onClick={toggle}
            >
                {selected ? (
                    <span className={'page-pbc-picker-btn__content'}>
                        <span className={'page-pbc-picker-btn__value'}>{selected.title}</span>
                        {selected.description && <span className={'page-pbc-picker-btn__meta'}>{selected.description}</span>}
                    </span>
                ) : (
                    <span className={'page-pbc-picker-btn__value'}>{placeholder}</span>
                )}
            </Button>
            {position && createPortal(
                <div ref={menuRef} onBlur={closeWhenFocusLeaves}>
                    <Menu
                        className={'page-pbc-menu'}
                        aria-labelledby={triggerId}
                        style={{ top: position.top, bottom: position.bottom, maxHeight: position.maxHeight, left: position.left, width: position.width }}
                    >
                        <div className={'page-pbc-menu__list'}>
                            {requests.map(request => (
                                <MenuItem
                                    key={request.id}
                                    className={'page-pbc-menu-item'}
                                    active={selected?.id === request.id}
                                    tabIndex={-1}
                                    onClick={() => choose(request)}
                                >
                                    <span className={'page-pbc-menu-item__text'}>
                                        <span className={'page-pbc-menu-item__heading'}>
                                            <span className={'page-pbc-menu-item__title'}>{request.title}</span>
                                            {request.status && <Tag type={'secondary'}>{request.status}</Tag>}
                                        </span>
                                        {request.description && <span className={'page-help-text'}>{request.description}</span>}
                                    </span>
                                </MenuItem>
                            ))}
                        </div>
                        {selected && (
                            <div className={'page-pbc-menu__footer'}>
                                <MenuItem tabIndex={-1} leading={'icon'} icon={<FontAwesomeIcon icon={faXmark} />} onClick={() => choose(undefined)}>
                                    {clearLabel}
                                </MenuItem>
                            </div>
                        )}
                    </Menu>
                </div>,
                // The design-system tokens are defined on the themed app root, not on :root, so the menu must stay inside it.
                triggerRef.current?.closest<HTMLElement>('[data-theme]') ?? document.body
            )}
        </div>
    );
};
